import { useEffect, useState } from 'react';
import { AppRenderer } from './renderer/AppRenderer';
import type { AppConfig } from '@devfactory/core';

export function RuntimeApp() {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadAppConfig() {
      try {
        // Get app ID from URL or subdomain
        const appId = getAppIdFromUrl();
        const response = await fetch(`/api/v1/apps/${appId}/config`);

        if (!response.ok) {
          throw new Error('Failed to load app configuration');
        }

        const config = await response.json();
        setAppConfig(config.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    }

    loadAppConfig();
  }, []);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !appConfig) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Error</h1>
          <p className="mt-2 text-gray-600">{error || 'App not found'}</p>
        </div>
      </div>
    );
  }

  return <AppRenderer config={appConfig} />;
}

function getAppIdFromUrl(): string {
  // Try to get from subdomain first
  const hostname = window.location.hostname;
  const parts = hostname.split('.');

  if (parts.length > 2 && parts[0]) {
    return parts[0];
  }

  // Fallback to path
  const path = window.location.pathname;
  const match = path.match(/^\/apps\/([^/]+)/);

  return match?.[1] || 'default';
}
